import React, { useState } from "react";
import { LayoutDashboard, GraduationCap, Receipt, UserPlus, Wallet, LifeBuoy, FileEdit, CreditCard, BadgeCheck, Users, ArrowRight, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";

export const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function SectionHead({ eyebrow, title, sub, light = false }) {
  return (
    <div className="max-w-2xl mb-12">
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-amber mb-3">{eyebrow}</div>
      <h2 className={`font-heading text-3xl md:text-4xl font-bold leading-tight ${light ? "text-white" : "text-navy"}`}>{title}</h2>
      {sub && <p className={`mt-3 text-base ${light ? "text-white/70" : "text-muted-foreground"}`}>{sub}</p>}
    </div>
  );
}

const BENEFITS = [
  [LayoutDashboard, "Digital Institute Dashboard", "Manage student enrollments and track their progress."],
  [GraduationCap, "Professional Training Programs", "Offer courses published and authorized by VIRIKS."],
  [Receipt, "Transparent Commission Tracking", "View eligible earnings and transaction history."],
  [UserPlus, "Simplified Enrollment", "Register students through a centralized digital workflow."],
  [Wallet, "Withdrawal Management", "Submit eligible commission withdrawal requests."],
  [LifeBuoy, "Partner Support", "Access available onboarding information and support channels."],
];

export function Benefits() {
  return (
    <section id="benefits" className="max-w-7xl mx-auto px-6 py-24" data-testid="fr-benefits">
      <div className="grid lg:grid-cols-[1fr_420px] gap-12 items-end mb-4">
        <SectionHead eyebrow="Why partner with VIRIKS" title="Everything your institute needs to run professional programs" sub="One platform for courses, enrollments, commissions and payouts — so you can focus on teaching." />
        <img src="/franchise/meeting.jpg" alt="Institute team discussing a training plan" loading="lazy" className="hidden lg:block rounded-2xl h-56 w-full object-cover mb-12" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {BENEFITS.map(([Icon, t, d], i) => (
          <div key={t} className="group bg-white border border-border rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300" style={{ animationDelay: `${i * 60}ms` }} data-testid={`fr-benefit-${i}`}>
            <div className="h-12 w-12 rounded-xl bg-amber/10 text-amber flex items-center justify-center mb-5 group-hover:bg-amber group-hover:text-white transition-colors"><Icon size={22} /></div>
            <h3 className="font-heading text-lg font-bold text-navy">{t}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  [FileEdit, "Apply Online", "Submit your institute details and application."],
  [CreditCard, "Complete Registration", "Pay the franchise fee and provide the required documents."],
  [BadgeCheck, "Get Approved", "Complete administrative review and receive partner access."],
  [Users, "Enroll Students", "Register students, monitor eligible commissions, and request withdrawals."],
];

export function HowItWorks() {
  return (
    <section className="bg-[#111111] text-white" data-testid="fr-steps">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <SectionHead light eyebrow="How it works" title="From application to your first enrollment in four steps" />
        <ol className="relative grid md:grid-cols-4 gap-10 md:gap-6">
          <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px bg-white/15" />
          <div className="md:hidden absolute top-0 bottom-0 left-7 w-px bg-white/15" />
          {STEPS.map(([Icon, t, d], i) => (
            <li key={t} className="relative flex md:flex-col gap-5 md:gap-0" data-testid={`fr-step-${i}`}>
              <div className="relative z-10 h-14 w-14 shrink-0 rounded-full bg-amber flex items-center justify-center shadow-[0_0_0_8px_#111111]"><Icon size={22} /></div>
              <div className="md:mt-6">
                <div className="text-xs font-mono text-white/40">STEP 0{i + 1}</div>
                <h3 className="font-heading text-xl font-bold mt-1">{t}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function CommissionModel({ pct = 30 }) {
  const [rev, setRev] = useState(10000);
  const inst = Math.round(rev * pct) / 100;
  return (
    <section className="max-w-7xl mx-auto px-6 py-24" data-testid="fr-commission">
      <SectionHead eyebrow="Commission model" title={`A clear ${100 - pct}:${pct} revenue share`} sub="Training is delivered by VIRIKS trainers, so VIRIKS retains the larger share. Commission is calculated on eligible commissionable revenue, as defined in the franchise terms — not automatically on every gross customer payment." />
      <div className="grid lg:grid-cols-2 gap-8 items-stretch">
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl p-7 bg-[#E9F7EF]" data-testid="fr-share-viriks"><div className="font-heading text-5xl font-black text-[#176B45]">{100 - pct}%</div><div className="font-bold text-navy mt-2">VIRIKS</div><p className="text-sm text-navy/70 mt-1">Training by VIRIKS trainers, platform and certification</p></div>
          <div className="rounded-2xl p-7 bg-[#E8F4FF]" data-testid="fr-share-institute"><div className="font-heading text-5xl font-black text-[#164A83]">{pct}%</div><div className="font-bold text-navy mt-2">Institute Centre</div><p className="text-sm text-navy/70 mt-1">Centre share of eligible commissionable revenue</p></div>
          <div className="col-span-2 h-4 rounded-full overflow-hidden flex"><div className="bg-[#176B45]" style={{ width: `${100 - pct}%` }} /><div className="bg-[#164A83] flex-1" /></div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-7" data-testid="fr-calculator">
          <div className="flex items-center gap-2 font-heading font-bold text-navy"><Calculator size={18} className="text-amber" /> Example calculator</div>
          <label className="text-sm text-muted-foreground mt-4 block">Eligible commissionable revenue (₹)</label>
          <Input type="number" min={0} value={rev} onChange={(e) => setRev(Math.max(0, Number(e.target.value)))} className="mt-1 text-lg font-semibold" data-testid="fr-calc-input" />
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between"><span>VIRIKS share ({100 - pct}%)</span><span className="font-bold text-[#176B45]" data-testid="fr-calc-viriks">{fmt(rev - inst)}</span></div>
            <div className="flex justify-between"><span>Institute centre share ({pct}%)</span><span className="font-bold text-[#164A83]" data-testid="fr-calc-institute">{fmt(inst)}</span></div>
          </div>
          <p className="text-xs text-muted-foreground mt-5">Illustration only. Actual eligibility is governed by the <Link to="/page/commission-policy" className="underline text-navy">commission policy</Link>.</p>
        </div>
      </div>
    </section>
  );
}

export function Investment({ fees, onApply }) {
  const items = ["Digital institute dashboard", "Student enrollment management", "Commission tracking", "Eligible commission withdrawal facility", "Access to authorized VIRIKS programs, subject to approval and agreement"];
  return (
    <section id="investment" className="relative overflow-hidden" data-testid="fr-investment">
      <img src="/franchise/lab.jpg" alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#111111]/85" />
      <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <SectionHead light eyebrow="Franchise investment" title="One registration fee. A complete digital institute." sub="The applicable total including GST is shown before you pay." />
        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          <div className="text-sm font-semibold text-amber">VIRIKS Institute Franchise</div>
          <div className="flex items-end gap-2 mt-2"><span className="font-heading text-5xl font-black text-navy" data-testid="fr-fee">{fmt(fees?.fee ?? 25000)}</span><span className="text-muted-foreground mb-2">+ GST ({fees?.gst_percent ?? 18}%)</span></div>
          <ul className="mt-6 space-y-3">{items.map((t) => <li key={t} className="flex gap-3 text-sm text-navy"><BadgeCheck size={18} className="text-amber shrink-0" />{t}</li>)}</ul>
          <button onClick={() => onApply("investment")} className="mt-8 w-full rounded-full viriks-amber text-white font-semibold py-3.5 inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity" data-testid="fr-invest-apply">Apply for Franchise <ArrowRight size={16} /></button>
        </div>
      </div>
    </section>
  );
}
